import type { BasketItem } from '../types/order';
import { isOwnAutoFeeLine } from './autoFee';

/**
 * Αλλαγές γραμμών του καλαθιού Take Away, γραμμένες για `setBasket(prev => …)`: παίρνουν ΠΑΝΤΑ
 * το τρέχον καλάθι, ποτέ το αντίγραφο της τελευταίας ζωγραφιάς.
 *
 * ΓΙΑΤΙ (24/10/2026, έγκριση ιδιοκτήτη): τα +/−/× έγραφαν `setBasket(basket.map(…))`. Όταν στην
 * ουρά του React περίμενε ακόμη μια προσθήκη που δεν είχε ζωγραφιστεί (προσθήκη μετά από αίτημα
 * δικτύου, δύο πατήματα πριν από τη ζωγραφιά), το React την εφάρμοζε και αμέσως μετά την
 * αντικαθιστούσε με το παλιό αντίγραφο: το είδος χανόταν σιωπηλά — λιγότερη χρέωση, τίποτα στην
 * κουζίνα. Οι κανόνες (ποσότητα ≥ 1, τιμές, ένωση ίδιων γραμμών) είναι ΑΚΡΙΒΩΣ οι ίδιοι με πριν·
 * αλλάζει μόνο ότι εφαρμόζονται στο τρέχον καλάθι.
 */

/** ± στη γραμμή: ποτέ κάτω από 1 (για αφαίρεση υπάρχει το ×), όπως πάντα. */
export function changeLineQuantity(basket: BasketItem[], itemId: string, delta: number): BasketItem[] {
  return basket.map(item => (item.id === itemId ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
}

/** ± στη γραμμή του Delivery: εκεί το − στο 1 αφαιρεί τη γραμμή (ποσότητα 0 = φεύγει), όπως πάντα. */
export function changeLineQuantityOrRemove(basket: BasketItem[], itemId: string, delta: number): BasketItem[] {
  return basket.map(item => {
    if (item.id !== itemId) return item;
    const quantity = Math.max(0, item.quantity + delta);
    return quantity === 0 ? null : { ...item, quantity };
  }).filter(Boolean) as BasketItem[];
}

/** × στη γραμμή. */
export function removeBasketLine(basket: BasketItem[], itemId: string): BasketItem[] {
  return basket.filter(item => item.id !== itemId);
}

/**
 * «Σε αναμονή» που ΠΕΤΥΧΕ: φεύγουν οι γραμμές που πήγαν στο πρόχειρο (`parked`, το καλάθι τη στιγμή
 * του πατήματος) — και μόνο τόσα τεμάχια όσα πήγαν. Ό,τι μπήκε όσο περίμενε ο server (νέα γραμμή, ή
 * +1 που ενώθηκε σε παρκαρισμένη γραμμή) μένει στο καλάθι. Χωρίς αλλαγές στο μεταξύ → άδειο καλάθι,
 * όπως πριν. Το αυτόματο τέλος των ειδών που μένουν το ξαναβρίσκει ο κανόνας (utils/autoFee).
 */
export function removeParkedLines(current: BasketItem[], parked: BasketItem[]): BasketItem[] {
  const parkedQty = new Map(parked.map(item => [item.id, item.quantity] as const));
  const left: BasketItem[] = [];
  for (const item of current) {
    const gone = parkedQty.get(item.id);
    if (gone === undefined) left.push(item);
    else if (item.quantity - gone > 0) left.push({ ...item, quantity: item.quantity - gone });
  }
  return left;
}

/** Επεξεργασία γραμμής: αλλάζουν ΜΟΝΟ τα πεδία της επεξεργασίας — η ποσότητα μένει η τρέχουσα. */
export function patchBasketLine(basket: BasketItem[], itemId: string, patch: Partial<BasketItem>): BasketItem[] {
  return basket.map(item => (item.id === itemId ? { ...item, ...patch } : item));
}

/**
 * «Αναίρεση» αφαίρεσης γραμμής ή αδειάσματος: ξαναμπαίνουν οι γραμμές του αντιγράφου (`snapshot`,
 * το καλάθι τη στιγμή του ×) που λείπουν από το τρέχον, στη θέση που είχαν. Ό,τι υπάρχει ακόμη
 * μένει όπως είναι ΤΩΡΑ (π.χ. ένα + μετά το ×) και ό,τι μπήκε μετά το × μένει στο τέλος.
 * Ως 24/10/2026 το αντίγραφο αντικαθιστούσε όλο το καλάθι και έσβηνε είδος που είχε μπει μέσα στα 4″.
 *
 * Χωρίς αλλαγές στο μεταξύ το αποτέλεσμα είναι ΑΚΡΙΒΩΣ το αντίγραφο (ίδια σειρά, ίδιες γραμμές).
 * Η αυτόματη γραμμή τέλους (`auto-fee-16-…`) του αντιγράφου ΔΕΝ ξαναμπαίνει όταν το καλάθι έχει
 * ήδη δική του — θα γινόταν διπλό τέλος· την ποσότητα της μίας τη βρίσκει ο κανόνας (utils/autoFee).
 */
export function restoreRemovedLines(current: BasketItem[], snapshot: BasketItem[]): BasketItem[] {
  const currentById = new Map(current.map(item => [item.id, item] as const));
  const snapshotIds = new Set(snapshot.map(item => item.id));
  const currentHasAutoFee = current.some(isOwnAutoFeeLine);
  const restored: BasketItem[] = [];
  for (const item of snapshot) {
    const now = currentById.get(item.id);
    if (now) restored.push(now);
    else if (!(currentHasAutoFee && isOwnAutoFeeLine(item))) restored.push(item);
  }
  for (const item of current) {
    if (!snapshotIds.has(item.id)) restored.push(item);
  }
  return restored;
}
